// TerrainShadows — Set15 P8 (§6.2): locked-viewpoint shadow mapping for the 3D terrain.
//
// The terrain fragment is a custom `ShaderMaterial` whose GLSL is ASSEMBLED (ShaderAssembler)
// and ALSO compiled standalone in the Node lane (`compileRawFragmentProgram`). It can therefore
// NOT use three's `#include <shadowmap_pars_fragment>` / `shadowmask_pars_fragment` chunks:
// those are GLSL1-flavoured (they use `texture2D`/`texture`), depend on `<packing>`/`<common>`
// being injected by three's prefix (absent from the standalone raw compile), and read the
// auto-uploaded struct uniforms (`directionalLightShadows[i]` etc.) which only exist when
// `material.lights === true` (which the terrain deliberately does not set).
//
// So this module owns the shadow SAMPLING GLSL (declared in `LIGHTING_GLSL`) + the module-global
// shadow light that the renderer pushes per-frame (mirroring how `u_lightDir`/`u_cameraPos` are
// set manually). The shadow uniforms are declared in the assembled shader and set on each chunk
// material from this module's global — the 2D path is a byte-identical no-op via `u_shadowEnabled`.
//
// Approach: ONE tight near-field directional shadow map (v1; cascades are a future generalisation
// — §6.2). The `Map3D_SceneLights` component owns the cast-shadow directional light + the renderer
// `gl.shadowMap` state + an orthographic shadow camera; this module resolves the knob values and
// exposes the global the renderer reads to set per-chunk uniforms.
import * as THREE from "three";
import {resolveBudgetNumber} from "../Scenery/@SceneryBudgetConfig.js";

// ── Knob defaults + validated resolvers (§8 R1) ──────────────────────────────────────────────
/** Shadow maps enabled in 3D (default true — one-time render, static scene+light+camera ⇒ cheap). */
export const DEFAULT_SHADOWS_ENABLED = true;
/** Near-field shadow map resolution (px per side). The locked camera pins where the legible near
 *  field is, so a single tight map over it suffices (§6.2). */
export const DEFAULT_SHADOW_MAP_SIZE = 2048;
/** Ortho shadow-camera half-extent (metres) around the eye — the near field the single map covers. */
export const DEFAULT_SHADOW_RADIUS = 600;
/** Directional shadow depth-bias. NEGATIVE (three's compare is `coord.z += bias` then
 *  `step(coord.z, depth)` → positive = more self-shadowing). -0.0015 clears acne over the
 *  ortho-cascade depth range (near 1 → far 2000) without visible peter-panning. */
export const DEFAULT_SHADOW_BIAS = -0.0015;
/** Shadow-map size floor (px) — smaller is pointless and a 0 would break the render target. */
const SHADOW_MAP_SIZE_FLOOR = 128;

/** Round a positive number down to the nearest power of two (three needs POT shadow-map dims;
 *  guarantees a sane RT). */
export function roundDownToPowerOf2(v: number): number {
	let size = 1;
	while (size * 2 <= v) size *= 2;
	return size;
}

/** Resolve `view3D_shadowMapSize` to a validated power-of-two-ish positive integer (§8 R1).
 *  Absent/NaN/≤0 → default; clamped to the floor. */
export function resolveShadowMapSize<T = number>(v: number|n|undefined, resultIfVIsNull: number|T = DEFAULT_SHADOW_MAP_SIZE): number|T {
	const resolved = resolveBudgetNumber(v, resultIfVIsNull);
	if (typeof resolved != "number") return resolved; // this only happens if v was null; return early (result = resultIfVIsNull)
	return Math.max(roundDownToPowerOf2(resolved), SHADOW_MAP_SIZE_FLOOR);
}

/** Resolve `view3D_shadowRadius` to a validated positive finite number (§8 R1). */
export function resolveShadowRadius<T = number>(v: number|n|undefined, resultIfVIsNull: number|T = DEFAULT_SHADOW_RADIUS): number|T {
	return resolveBudgetNumber(v, resultIfVIsNull);
}

/** Resolve `view3D_shadowBias` to a validated finite number (§8 R1).  Absent/NaN/±Infinity →
 *  default.  NOTE: unlike the radius/map-size knobs, this is NOT clamped to a sign/range — a user
 *  must be able to reach any finite value (a positive bias is a valid, if acne-prone, choice). */
export function resolveShadowBias<T = number>(v: number|n|undefined, resultIfVIsNull: number|T = DEFAULT_SHADOW_BIAS): number|T {
	if (typeof v != "number" || !Number.isFinite(v)) return resultIfVIsNull;
	return v;
}

/** Resolve `view3D_shadowsEnabled` (bool; non-bool → default). */
export function resolveShadowsEnabled<T = boolean>(v: boolean|n|undefined, resultIfVIsNull: boolean|T = DEFAULT_SHADOWS_ENABLED): boolean|T {
	return typeof v == "boolean" ? v : resultIfVIsNull;
}

// ── The module-global shadow light ──────────────────────────────────────────────────────────
/** The current 3D shadow directional light, set per-frame by `Map3D_SceneLights` (so the renderer
 *  can read its matrix + shadow texture to set per-chunk uniforms WITHOUT importing the light
 *  component). Mirrors `terrainQuadtree_current` (the cross-file-global pattern). Null = no
 *  shadow light (2D, or 3D with shadows disabled) — the renderer sets `u_shadowEnabled` 0. */
export let terrainShadowLight_current: THREE.DirectionalLight | null = null;
export function setTerrainShadowLight(l: THREE.DirectionalLight | null): void {
	terrainShadowLight_current = l;
}
/** The shadow-map size the current shadow light was created with (for setting u_shadowMapSize). */
export let terrainShadowMapSize_current = DEFAULT_SHADOW_MAP_SIZE;
export function setTerrainShadowMapSize(s: number): void { terrainShadowMapSize_current = s; }
